import { useState, useCallback } from "react";
import {
  getAgentInfo,
  getTurnstileConfig,
  validateAccess,
  sendChatMessage,
  resetConversation,
  processPublicAgentError,
} from "../services/PublicAgentService";

const usePublicAgent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [isValidated, setIsValidated] = useState(false);

  const validatePhoneAccess = useCallback(
    async (token, phone, turnstileToken) => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await validateAccess(token, {
          turnstileToken,
        });

        if (result.success) {
          setSessionToken(result.sessionToken);
          setIsValidated(true);
          return {
            success: true,
            sessionToken: result.sessionToken,
          };
        } else {
          setError(result.error);
          return {
            success: false,
            error: result.error,
            errorCode: result.errorCode,
          };
        }
      } catch (error) {
        const errorMessage = "Erro inesperado na validação";
        setError(errorMessage);
        return {
          success: false,
          error: errorMessage,
          errorCode: "UNEXPECTED_ERROR",
        };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const getAgent = useCallback(async (token) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getAgentInfo(token);

      if (result.success) {
        return {
          success: true,
          agent: result.agent,
          isValid: result.isValid,
        };
      } else {
        setError(result.error);
        return {
          success: false,
          error: result.error,
          errorCode: result.errorCode,
          isValid: result.isValid,
        };
      }
    } catch (error) {
      const errorMessage = "Erro ao carregar agente";
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
        errorCode: "AGENT_LOAD_ERROR",
        isValid: false,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendMessage = useCallback(
    async (token, messageData) => {
      setError(null);

      try {
        let payload;
        
        // Se for FormData (áudio), não destruturar para preservar o tipo
        if (messageData instanceof FormData) {
          payload = messageData;
          // Adicionar sessionToken ao FormData se necessário
          if (!messageData.has('sessionToken') && sessionToken) {
            messageData.append('sessionToken', sessionToken);
          }
        } else {
          // Para mensagens de texto, criar objeto normal
          payload = {
            ...messageData,
            sessionToken: messageData.sessionToken || sessionToken,
          };
        }

        const result = await sendChatMessage(token, payload);
        return result;
      } catch (error) {
        setError(error.message);
        throw error;
      }
    },
    [sessionToken]
  );

  const resetChat = useCallback(
    async (token, data) => {
      setIsLoading(true);
      setError(null);

      try {
        const payload = {
          ...data,
          sessionToken: data.sessionToken || sessionToken,
        };

        const result = await resetConversation(token, payload);
        return result;
      } catch (error) {
        const errorMessage = "Erro ao resetar conversa";
        setError(errorMessage);
        return {
          success: false,
          error: errorMessage,
        };
      } finally {
        setIsLoading(false);
      }
    },
    [sessionToken]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearSession = useCallback(() => {
    setSessionToken(null);
    setIsValidated(false);
    setError(null);
  }, []);

  const getTurnstileConfiguration = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getTurnstileConfig();
      return result;
    } catch (error) {
      const errorMessage = "Erro ao carregar configuração de segurança";
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
        config: null,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    error,
    sessionToken,
    isValidated,
    validatePhoneAccess,
    getAgent,
    sendMessage,
    resetChat,
    getTurnstileConfiguration,
    clearError,
    clearSession,
  };
};

export default usePublicAgent;
